import { inject } from '@angular/core';
import { asyncScheduler, observeOn, of, tap } from 'rxjs';

import { TranslateService } from '../services/translate.service';
import type { AddTranslationLoaderArgs } from '../services/types';

export type KeyAndLoader = Omit<AddTranslationLoaderArgs, 'type'>;

/** Decorator will attempt to register translation source on the
 * `TranslateService.service` static property upon evaluation.
 *
 * When paired with translation route guards, this allows for deferred
 * navigation until the target's translation dependencies have been loaded.
 *
 * **Note**: If the static property is nullish, *no loaders will be registered*.
 * Therefore, in order for translations to be registered properly,
 * `TranslationService` should already be available. Usually this
 * is done by injecting it at the application root (app component).
 * */
export function registerTranslationSource({
  loader,
  key,
  type,
}: AddTranslationLoaderArgs) {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters, @typescript-eslint/no-explicit-any
  return function <T extends { new (...args: any[]): object }>(_constr: T) {
    const loaderArgs = { loader, key, type };
    // Schedule check on the (macro)task queue so that we maximize the chance of the TranslateService
    // static property being truthy. Otherwise, some loaders might not be registered.
    of(null)
      .pipe(
        observeOn(asyncScheduler),
        tap(() => {
          const isStaticDefined = TranslateService.service as
            | TranslateService
            | undefined;
          if (isStaticDefined) {
            TranslateService.service.addTranslationLoader(loaderArgs);
          }
        }),
      )
      .subscribe();
  };
}

/** Register translation source that can override existing translations. */
export function registerPluginTranslation(args: KeyAndLoader) {
  return registerTranslationSource({ ...args, type: 'update' });
}

/** Register translation source that will supplement existing translations. */
export function registerLibTranslation(args: KeyAndLoader) {
  return registerTranslationSource({ ...args, type: 'supplement' });
}

/** Module that registers translation source upon its initialization.
 * It can be combined together with one of available translation loader decorators. In this way, even if the translation dependency
 * is not registered on initial evaluation, it will still be registered
 * upon loading.
 *
 * **Note:** Avoid defining multiple translation modules in a single file.
 * Since these modules are supposed to have immediate side-effects,
 * this can cause all of the defined translation sources to be loaded
 * once a single one is imported.
 * Using the module approach is therefore more suited in libs than in apps.
 *
 * **Usage examples:**
 *
 ```ts
// General
⁣@NgModule()
⁣@registerTranslationSource(args)
export class TranslateModule extends ConfigureTranslateModule.config(args) {}

// Plugin
⁣@NgModule()
⁣@registerPluginTranslation(args)
export class TranslateModule extends ConfigureTranslateModule.configurePlugin(args) {}

// Lib
⁣@NgModule()
⁣@registerLibTranslation(args)
export class TranslateModule extends ConfigureTranslateModule.configureLib(args) {}
``` */
export class ConfigureTranslateModule {
  static configure(args: AddTranslationLoaderArgs) {
    return class {
      constructor() {
        inject(TranslateService).addTranslationLoader(args);
      }
    };
  }
  static configurePlugin(args: KeyAndLoader) {
    return class {
      constructor() {
        inject(TranslateService).addTranslationLoader({
          ...args,
          type: 'update',
        });
      }
    };
  }
  static configureLib(args: KeyAndLoader) {
    return class {
      constructor() {
        inject(TranslateService).addTranslationLoader({
          ...args,
          type: 'supplement',
        });
      }
    };
  }
}
